// Shapes of the collector's /post-purchase admin API
// (sbly-dash-collector src/sbly-api-server/postPurchase/postPurchaseAdminController.js).

export interface Upsell {
  id: string;
  priority: number;
  description: string;
  // A swap replaces items on the order; an add-on adds products to it.
  kind: 'swap' | 'add_on';
  designId: string;
  layout: string;
  trigger: { sku: string; units: number; deal: string };
  target: { sku: string; units: number };
  enabled: boolean;
  staffOnly: boolean;
  // The upsell's A/B tests, newest first (built on this dashboard).
  experiments?: ExperimentReport[];
  stockRule?: StockRule;
  // Where the target is stocked now; null centers when ShipBob could not be read.
  targetStock?: { centers: { center: string; region: string; fulfillable: number }[] | null; error: string | null };
}

export type ExperimentStatus = 'draft' | 'running' | 'stopped';

// An upsell's A/B test (collector src/ecommerce/postPurchase/experiment.js):
// the versions it compares (design ids), its split, its state, and each
// version's standing while it ran. Money in cents. Firebase keeps no nulls, so
// an unset field may be missing. `error`: its report could not be read.
export interface ExperimentReport {
  id: string;
  upsellId: string;
  label: string;
  versions: string[];
  weights: Record<string, number>;
  status: ExperimentStatus;
  forceVersion?: string | null;
  createdAt: string;
  startedAt?: string | null;
  stoppedAt?: string | null;
  plannedViewsPerArm?: number;
  // Below this many views on every version the odds are not given (null).
  minViewsToRead?: number;
  problem?: string | null;
  error?: string;
  arms?: {
    designId: string;
    label: string;
    runnable: boolean;
    weight: number;
    chargeCents: number | null;
    profitPerAcceptCents: number | null;
    viewed: number;
    accepted: number;
    swapped: number;
    revenue: number;
    chanceBest: number | null;
    profitPerViewCents: { low: number; mid: number; high: number } | null;
  }[];
}

// How a version looked when it was recorded (collector versions.js): the page
// as the API rendered it then, and its image.
export interface VersionLook {
  display: NonNullable<UpsellPreview['display']>;
  imageUrl: string | null;
  imageDescription?: string;
  chargeCents: number;
  at: string;
}

// One version of an upsell that shipped (GET /upsells/:id/versions).
export interface UpsellVersion {
  designId: string;
  label: string;
  source: 'current' | 'candidate' | 'backfill';
  firstSeen: string | null;
  look: VersionLook | null;
  // The upsell as written in upsells.js today.
  current: boolean;
  // Still passes today's checks, so a test may show it.
  runnable: boolean;
  stats: DesignStats | null;
}

// Which ShipBob centers must hold an upsell's target for it to be offered
// (collector src/ecommerce/postPurchase/stock.js).
export type StockRule = 'same_center' | 'nearest' | 'region' | 'any' | 'off';

export interface DesignStats {
  // Null on error events recorded before the request named an offer.
  upsellId: string | null;
  designId: string | null;
  // A breakdown's values (BREAKDOWN_DIMENSIONS), on rows broken down by them.
  day?: string;
  landingPage?: string;
  cart?: string;
  matchedRule?: string;
  offered: number;
  viewed: number;
  accepted: number;
  declined: number;
  failed: number;
  swapped: number;
  // An add-on's products read back off the order after accepting.
  added: number;
  // Still unswapped. One a later attempt completed counts as swap_recovered.
  swap_failed: number;
  swap_recovered?: number;
  errors: number;
  revenue: number;
  // Contribution margin in dollars: the actual box freight for orders that have
  // shipped, the estimate for the rest; null when a product has no COGS.
  margin: number | null;
  // The same, every order on the estimate; and the share of accepts on actual freight.
  marginEstimate?: number | null;
  marginShippedShare?: number | null;
  firstSeen: string;
  lastSeen: string;
}

// A dosaze.com customer who sees staff-only upsells. name and email are null for
// one added before the dashboard kept them.
export interface StaffCustomer {
  customerId: number;
  name: string | null;
  email: string | null;
}

// One upsell's totals on one Pacific date, across its designs.
export interface DailyTotal {
  date: string;
  upsellId: string;
  viewed: number;
  accepted: number;
  revenue: number;
  // Actual freight where shipped, else estimated (see DesignStats).
  margin: number | null;
  marginEstimate?: number | null;
  marginShippedShare?: number | null;
}

// The page's period: the last `days` days, or a range of Pacific dates.
export type Period = { days: number } | { from: string; to: string };

export interface Feature {
  id: string;
  label: string;
  description: string;
  enabled: boolean;
  staffOnly: boolean;
}

export interface UpsellsResponse {
  // Null for a date range.
  days: number | null;
  // The period as Pacific dates, inclusive.
  from: string;
  to: string;
  daily: DailyTotal[];
  // Per date: the store's orders (null on the test store), those that could see the page (not paid with Apple Pay,
  // Google Pay, Shop Pay Installments, Affirm, Afterpay, Klarna or Shop Cash, and not $0), the store's revenue
  // (current subtotal, before tax), checkouts offered an upsell, and checkouts that saw the page.
  coverage: { date: string; orders: number | null; eligibleOrders?: number | null; revenue?: number | null; offered: number; viewed: number }[];
  // Freight per added item behind the add-ons' margins; problem when margins are unknown.
  economics: {
    freightPerUnit: number | null;
    // Per swap upsell: its box's extra freight per swap (null until enough are invoiced) and how many.
    swapFreight: Record<string, { perSwap: number | null; sample: number }>;
    problem: string | null;
    // Share of the period's accepts whose margin uses their boxes' actual freight; null when unread.
    marginShippedShare?: number | null;
    // Why actual shipping couldn't be read (every margin is then the estimate).
    shippingProblem?: string | null;
  };
  // Whose stats these are: 'US' (dosaze.com) or 'DEV' (the test store).
  store: string;
  stores: string[];
  maxOffersPerCheckout: number;
  // Switches that are not one upsell's: the second offer after a decline, the thank-you page offer. Missing
  // until the API with them is live.
  features?: Feature[];
  // Who sees staff-only upsells, in the order they were added.
  staff: StaffCustomer[];
  upsells: Upsell[];
  stats: DesignStats[];
}

// An accepted upsell whose swap never landed: the customer paid the upsell
// charge and the order still holds the replaced items (GET /post-purchase/unswapped).
export interface UnswappedOffer {
  orderId: number;
  orderName: string;
  createdAt: string; // ISO, UTC
  ageMinutes: number;
  slot: number;
  upsellId: string | null;
  chargeCents: number | null;
  // Still inside the swap's time limit: the reconciler is retrying it.
  fixable: boolean;
  detail: string | null;
  // On hold in Shopify (the swap holds the order when it gives up), so ShipBob won't ship it yet.
  onHold?: boolean;
}

export interface UnswappedResponse {
  days: number;
  store: string;
  domain: string | null;
  orders: UnswappedOffer[];
}

// One line of an upsell's price breakdown, as the API renders it.
export type PreviewLine = { separator: true } | { label: string; value: string; strike: boolean };

// An upsell's page as a qualifying dosaze.com cart would see it today
// (GET /post-purchase/preview). problem says why there is none.
export interface UpsellPreview {
  id: string;
  designId: string;
  layout: string;
  thankYouNote: string | null;
  problem: string | null;
  chargeCents?: number;
  imageUrl?: string | null;
  imageDescription?: string;
  display?: {
    layout: string;
    bannerTitle: string | null;
    bannerBody: string | null;
    heading: string | null;
    body: string | null;
    // Reasons to upgrade, shown with check marks; **bold** marks emphasis in any text.
    benefits?: string[];
    // Benefits right under the heading, above the body (else after it).
    benefitsUnderHeading?: boolean;
    lines: PreviewLine[];
    footnote: string | null;
    accept: string;
    decline: string;
  };
}

// The Orders tab (GET /post-purchase/orders): recent orders, what the upsells
// did on each, and what today's rules say they should have done.
export type OrderVerdict = 'none' | 'matched' | 'qualified_not_offered' | 'offered_not_qualified' | 'offered_other';

export interface OrderExplanation {
  orderId: number;
  orderName: string;
  createdAt: string;
  cancelled: boolean;
  paymentGateways: string[];
  // Apple Pay / Shop Pay / Google Pay when a wallet paid, else the gateway; null when unknown or not looked up.
  paymentMethod?: string | null;
  items: { sku: string | null; quantity: number; paidCents: number }[];
  // The upsells today's rules offer, in offer order.
  expected: string[];
  offered: { upsellId: string; designId: string | null; outcome: string | null }[];
  verdict: OrderVerdict;
  decisions: {
    upsellId: string;
    enabled: boolean;
    qualifies: boolean;
    reason: string | null;
    chargeCents: number | null;
    // The stock check per product, when the upsell got that far.
    stock: StockDiagnosis[] | null;
  }[];
}

// One center holding an upsell product, at today's stock.
export interface CenterStock {
  center: string;
  region: string;
  fulfillable: number;
  // From the customer's shipping address; null without one.
  miles: number | null;
}

// The stock check on one product of an upsell, for one order (collector stock.js stockDiagnosis).
export interface StockDiagnosis {
  sku: string;
  units: number;
  rule: StockRule;
  // Null when the rule is met.
  refusal: string | null;
  error?: string;
  // The customer's region, and the nearest center holding what they bought.
  region: string | null;
  homeCenter: string | null;
  stocked: CenterStock[];
  // The centers the rule lets it ship from, nearest first.
  eligible: CenterStock[];
}

export interface OrdersResponse {
  hours: number;
  store: string;
  domain: string;
  orders: OrderExplanation[];
}

// An order that accepted one design (GET /post-purchase/upsells/:id/takers).
export interface Taker {
  checkoutToken: string;
  orderId: number | null;
  orderName: string | null;
  charge: number | null;
  at: string;
  // swapped / added / swap_failed, accepted when nothing came after yet, or
  // failed (payment) / error when it went wrong before a charge.
  outcome: string;
  // The problem's own words, when it went wrong.
  detail: string | null;
}

// Where an upsold order's comparable box cost came from (collector shippingActuals.js BASIS).
export type ComparableBasis = 'same_center_state' | 'same_center_region' | 'same_center' | 'any_center' | 'benchmark';
export type ShippingStatus = 'shipped' | 'not shipped yet' | 'no comparable orders';

// One upsold order with its acceptance's costs (GET /post-purchase/shipping). Dollars.
export interface UpsoldOrderShipping {
  orderId: number;
  orderName: string | null;
  financialStatus: string | null;
  upsellId: string;
  designId: string;
  acceptedAt: string; // UTC, no zone
  day: string;
  // False while only accepted: no swapped / added row yet.
  confirmed: boolean;
  charge: number;
  extraCogs: number | null;
  fee: number;
  // The extra freight the margins estimate before an order ships.
  estimatedFreight: number | null;
  estimatedMargin: number | null;
  // What ShipBob billed for all of the order's boxes, once every one is billed.
  boxCost: number | null;
  boxes: number;
  centers: string[];
  // The median box of orders with the same items without the upsell (or the unit-count benchmark).
  comparableBoxCost: number | null;
  comparableBasis: ComparableBasis | null;
  comparableSample: number | null;
  comparableCenter: string | null;
  // The destination the comparable orders share: a state, or a region (West / Central / East).
  comparableState: string | null;
  comparableRegion: string | null;
  // Where the order went (ShipBob's recipient state).
  state: string | null;
  actualExtraFreight: number | null;
  trueMargin: number | null;
  status: ShippingStatus;
}

export interface ShippingResponse {
  days: number | null;
  from: string;
  to: string;
  store: string;
  domain: string | null;
  orders: UpsoldOrderShipping[];
  problem: string | null;
}

// What Performance by design can break an offer down by: the field each value is on in
// GET /post-purchase/performance/breakdown's rows (collector src/ecommerce/postPurchase/breakdown.js).
export const BREAKDOWN_DIMENSIONS = {
  DAY: 'day',
  LANDING_PAGE: 'landingPage',
  CART: 'cart',
  MATCHED_RULE: 'matchedRule',
} as const;
export type BreakdownDimension = (typeof BREAKDOWN_DIMENSIONS)[keyof typeof BREAKDOWN_DIMENSIONS];

// One finest row of the breakdown: numbers that add up across rows, so the table sums
// them over the dimensions picked. Margins sum; the share on actual freight is
// derived from `shipped` (acceptances on actual freight; null when shipping wasn't read).
export interface BreakdownRow extends DesignStats {
  day: string;
  landingPage: string;
  cart: string;
  matchedRule: string;
  shipped: number | null;
}

// Performance by design for the table's own period, broken down (GET /post-purchase/performance/breakdown).
export interface BreakdownResponse {
  days: number | null;
  from: string;
  to: string;
  store: string;
  rows: BreakdownRow[];
  // Landing pages and carts beyond each upsell's most-viewed this many read "Other".
  topValues: Partial<Record<BreakdownDimension, number>>;
  // Of the checkouts that saw the page, how many have a known landing page and cart.
  coverage: { viewedCheckouts: number; withLandingPage: number; withCart: number };
  // Why its margins are unknown (no costs) or only the estimate (actual shipping unread), as /upsells' economics;
  // why landing pages read Unknown, or carts show SKUs.
  problem: string | null;
  shippingProblem: string | null;
  landingPageProblem: string | null;
  cartProblem: string | null;
}
